<i18n>
{
	"en": {
		"orders": "orders",
		"noOrders": "There are no orders on this trip yet.",
		"customerName": "Customer",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "From",
		"loadingMeters": "Loading",
		"numberOfPallets": "Pallets",
		"order": "Order",
		"status": "Status",
		"to": "To",
		"ediStatus": "EDI",
		"volume": "Volume",
		"weight": "Gr. Weight",
		"removeOrderAndMerge": "Remove",
		"editOrder": "Edit Order",
		"moveOrder": "Move Order to Different Trip",
		"ediScheduleOrder": "Schedule order for EDI",
		"ediUnScheduleOrder": "Unschedule order for EDI",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"removeOrder": "Remove, but keep stretch",
		"trip": "Trip {n}",
		"orderAddedToTrip": "Order added to trip {number}",
		"failedToAddOrderToTrip": "Failed to add order to trip {number}",
		"validationErrors": "Trip is currently invalid, please edit trip before adding orders.",
		"unableToUpdateTrip": "Could not update trip",
		"orderAlreadyAdded": "A selected order is already part of the selected trip."
	},
	"no": {
		"orders": "oppdrag",
		"noOrders": "Det er ingen oppdrag på denne turen ennå.",
		"customerName": "Kunde",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "Fra",
		"loadingMeters": "Lastm.",
		"numberOfPallets": "Kolli",
		"order": "Oppdrag",
		"status": "Status",
		"to": "Til",
		"ediStatus": "EDI",
		"volume": "Vol",
		"weight": "Br. vekt",
		"removeOrderAndMerge": "Fjern",
		"editOrder": "Rediger oppdrag",
		"moveOrder": "Flytt oppdrag til annen tur",
		"ediScheduleOrder": "Planlegg ordre for EDI",
		"ediUnScheduleOrder": "Fjern planlagt EDI sending",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"removeOrder": "Fjern og behold strekning",
		"trip": "Tur {n}",
		"failedToAddOrderToTrip": "Kunne ikke legge til oppdrag på turen {number}",
		"orderAddedToTrip": "Oppdrag lagt til tur {number}",
		"validationErrors": "Turen er ugyldig. Rediger turen før du legger til oppdrag.",
		"unableToUpdateTrip": "Kunne ikke oppdatere tur",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen."
	}
}
</i18n>
<template>
	<t-vertical-panel class="order-list">
		<div slot="header" class="order-list-header">
			<t-panel-header
				appearance="minimal"
				type="bp"
				icon="arrow-left"
				color="blue"
				:text="$t('trip', {n: tripDetails.tripNumber})"
				@click="setMapTripDetailsVisible(false)"
			/>

			<div class="order-list-summary">
				<t-metric-details
					:value="tripDetails.volume"
					:max="Number(getProperty(tripDetails.cargoCarrier, 'volume'))"
					decimals="1"
					unit="m3"
					size="small"
				/>
				<t-metric-details
					:value="tripDetails.grossWeight"
					:max="Number(getProperty(tripDetails.cargoCarrier, 'weight'))"
					unit="kg"
					size="small"
				/>
				<t-metric-details
					:value="tripDetails.loadingMeters"
					:max="Number(getProperty(tripDetails.cargoCarrier, 'loadingMeters'))"
					decimals="1"
					unit="lm"
					size="small"
				/>
			</div>
		</div>

		<div class="order-list-content">
			<t-grid
				v-if="ordersFound"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDef"
				:rowHeight="50"
				:items="orders"
				:selectableMode="['row']"
				selectable
				singleSelect
				class="cargo-information-grid"
				@rowDoubleClick="setEditOrder"
				@delete="onGridDelete"

				@selected="onRowsSelected"
				@filtered="onFiltered"
			>
				<template v-slot:status="{ item }">
					<div class="icon-container">
						<t-timpex-icon
							:icon="getStatusIcon(item)"
							:color="getStatusColor(item)"
							size="md"
						/>
						<t-pill
							v-if="item.deviations"
							size="small"
							color="red"
							style="position: absolute; top: -25%; right: 0;"
							isSolid
						>!</t-pill>
					</div>
				</template>
				<template v-slot:ediStatus="{item}">
					<div v-if="!isUnscheduled(item)" class="icon-container">
						<div v-if="isScheduled(item)">
							<t-timpex-icon
								:icon="'time'"
								:type="'bp'"
								:color="'orange'"
							/></div>
						<div v-if="isSent(item)">
							<t-timpex-icon
								:icon="'tick'"
								:type="'bp'"
								:color="'green'"
							/></div>
					</div>
				</template>
				<template v-slot:despatchParty="{ item }">
					<div v-if="item.despatchParty" style="height: 16px;">{{ item.despatchParty.name }}</div>
					<div v-if="item.despatchParty" class="grid-cell-sub-line">{{ item.despatchParty.street }}, {{ item.despatchParty.postalCode }} {{ item.despatchParty.postalPlace }}</div>
				</template>
				<template v-slot:deliveryParty="{ item }">
					<div v-if="item.deliveryParty" style="height: 16px;">{{ item.deliveryParty.name }}</div>
					<div v-if="item.deliveryParty" class="grid-cell-sub-line">{{ item.deliveryParty.street }}, {{ item.deliveryParty.postalCode }} {{ item.deliveryParty.postalPlace }}</div>
				</template>
				<template v-slot:customer="{ item }">
					<div v-if="item.customer" style="height: 16px;">{{ item.customer.name }}</div>
					<div v-if="item.customer" class="grid-cell-sub-line">{{ item.customer.customerNo }}</div>
				</template>
				<template v-slot:contextMenu="{ item }">
					<t-menu>
						<t-menu-group>
							<t-menu-item
								icon="edit"
								@click="setEditOrder(item.id)"
							>
								<strong>{{ $t('editOrder') }}</strong>
							</t-menu-item>
							<t-menu-item
								icon="inheritance"
								@click="showMoveLoadedOrder(item)"
							>
								{{ $t('moveOrder') }}
							</t-menu-item>
							<t-menu-item
								v-if="!isScheduled(item)"
								:disabled="!transporterHasEdipart()"
								icon="edi"
								@click="onScheduleOrder({ subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.id })"
							>
								{{ $t('ediScheduleOrder') }}
							</t-menu-item>
							<t-menu-item
								v-if="orderInstructionScheduled(item)"
								icon="edi"
								@click="onUnscheduleOrder({ subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.id })"
							>
								{{ $t('ediUnScheduleOrder') }}
							</t-menu-item>
							<hr>
							<t-menu-item
								icon="cross"
								@click="onRemoveOrders(item)"
							>
								{{ $t('removeOrderAndMerge') }}
							</t-menu-item>
							<t-menu-item
								icon="cross"
								@click="onRemoveOrders(item, true)"
							>
								{{ $t('removeOrder') }}
							</t-menu-item>
							<hr>
							<t-menu-item
								icon="package-label"
								@click="showSinglePackageLabel(item.orderId)"
							>
								{{ $t('packageLabel') }}
							</t-menu-item>
							<t-menu-item
								icon="waybill"
								@click="showSingleWaybill(item.orderId)"
							>
								{{ $t('waybill') }}
							</t-menu-item>
						</t-menu-group>
					</t-menu>
				</template>
			</t-grid>

			<t-no-content-panel
				v-show="!ordersFound"
				:text="$t('noOrders')"
				icon="order"
			/>
		</div>

		<ConfirmRemoveTripOrder
			:isShown="confirmRemoveTripOrderVisible"
			:trip="tripDetails"
			@close="toggleRemoveTripOrder(false)"
			@confirm="confirmRemoveOrdersFromTrip"
		/>
		<MoveOrderToDifferentTripSelect
			:isShown="moveLoadedOrderVisible"
			:currentTrip="tripDetails"
			:order="selectedOrder"
			:disableSubmit="moveLoadedOrderProcessing"
			@close="moveLoadedOrderVisible = false"
			@moveOrder="onMoveOrderToDifferentTrip"
		/>
	</t-vertical-panel>
</template>

<script>
import {
	mapDynamicState, mapDynamicActions
} from '@/core/mapDynamic';
import {
	displayDateFormat, storageDateFormat, displayDateFormatter, dateExists, goodsTypesIcons, statuses, eventBus, edi
} from '@/core';
import moment from 'moment';
import OrderStatusFilter from '@/modules/orderSearch/OrderStatusFilter';
import ConfirmRemoveTripOrder from '@/modules/tripSearch/modals/ConfirmRemoveTripOrder';
import MoveOrderToDifferentTripSelect from '@/modules/loadPlanningList/modals/MoveOrderToDifferentTripSelect';
import TMenuItem from '@/components/evergreen/menu/TMenuItem';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';

export default {
	name: 'TripViewOrderGrid',
	components: {
		TMetricDetails,
		TMenuItem,
		ConfirmRemoveTripOrder,
		MoveOrderToDifferentTripSelect
	},
	inject: ['namespace'],
	data() {
		return {
			rowsSelected: 0,
			mapPosition: null,
			observer: null,
			subHeaderObserver: null,
			subHeaderWidth: null,
			columnDefinitions: [],
			defaultColumnDef: { resizable: true },
			confirmRemoveTripOrderVisible: false,
			ordersToRemove: null,
			mergeOrder: false,
			moveLoadedOrderVisible: false,
			moveLoadedOrderProcessing: false,
			selectedOrder: {}
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace', ['selectedMapOrderIds', 'mapOrderIds']),
		...mapDynamicState('computedTripNamespace', ['tripDetails']),
		computedTripNamespace() {
			return `loadPlanning/${this.namespace.id}/tripSearch`;
		},
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		localStorageId() {
			return `loadPlanningOrderSearchList/${this.namespace.id}`;
		},
		orders() {
			let result = [];
			if (this.tripDetails.orders) {
				result = [...this.tripDetails.orders];
				result.sort((a,b) => {
					let aNum = isNaN(a.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : a.loadingSeqNo;
					let bNum = isNaN(b.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : b.loadingSeqNo;
					return aNum - bNum;
				});
			}

			if (this.selectedMapOrderIds && this.selectedMapOrderIds.length > 0) {
				let lookup = new Set(this.selectedMapOrderIds);
				return result.filter(x => lookup.has(x.id));
			}
			if (!this.mapOrderIds || this.mapOrderIds.length === result.length)
				return result;
			let lookup = new Set(this.mapOrderIds);
			return result.filter(x => lookup.has(x.id));
		},
		ordersFound() {
			return this.orders.length;
		},
		singleOrderSelected() {
			return this.rowsSelected === 1;
		},
		selectedOrders() {
			if (this.rowsSelected > 0) {
				return this.$refs.grid.getSelectedItems();
			}
			return [];
		},
	},
	beforeMount() {
		this.columnDefinitions = [
			{
				field: 'status',
				displayName: this.$t('status'),
				valueGetter: (item) => item.name,
				width: 50,
				resizable: false,
				customFilterComponent: OrderStatusFilter,
				headerFilterButtonOnly: true,
			},
			{
				field: 'orderNumber',
				displayName: this.$t('order'),
				resizable: false,
				width: 75,
				type: 'number',
				comparableValueGetter: (item) => Number(item.orderNumber),
			},
			{
				field: 'etd.date',
				displayName: this.$t('etd'),
				valueGetter: (item) => displayDateFormatter(this.dateGetter(item.atd, item.etd)),
				width: 80,
				resizable: false,
				type: 'date',
				dateFormat: displayDateFormat,
				comparableValueGetter: (item) => moment(this.dateGetter(item.atd, item.etd), storageDateFormat).startOf('day').toISOString(),
				cellClass(item) { return dateExists(item.atd) ? 'grid-cell-bold' : 'grid-cell-light'; }
			},
			{
				field: 'despatchParty',
				displayName: this.$t('from'),
				comparableValueGetter: (item) => this.addressGetter(item.despatchParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'deliveryParty',
				displayName: this.$t('to'),
				comparableValueGetter: (item) => this.addressGetter(item.deliveryParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'ediStatus',
				displayName: this.$t('ediStatus'),
				width: 100,
				type: 'edi',
			},
			{
				field: 'customer',
				displayName: this.$t('customerName'),
				comparableValueGetter: ({ customer }) => customer ? `${customer.name} ${customer.customerNo}` : '',
				minWidth: 125,
				type: 'customer',
			},
		];
	},
	async mounted() {
		await this.$nextTick();
		let map = document.querySelector('.map');
		if (map) {
			let setMapPosition = function() {
				let { x } = map.getBoundingClientRect();
				this.mapPosition = `position: fixed; display: flex; flex-wrap: no-wrap; right: ${x}; z-index: 999; background: white; padding: 14px; height:22px;`;
			}.bind(this);

			this.observer = new ResizeObserver(() => setMapPosition);
			this.observer.observe(map);
			setMapPosition();
		}
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
		if (this.subHeaderObserver) this.subHeaderObserver.disconnect();
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace',[
			'setSelectedGridOrderIds',
			'setFilteredGridOrderIds',
			'getWaybill',
			'getPackageLabel',
			'removeOrdersFromTrip',
			'unscheduleSubOrderForEdi',
			'scheduleSubOrderForEdi',
			'sendTripOnEdi',
			'moveOrderToDifferentTrip',
			'setMapTripDetailsVisible'
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
			this.setSelectedGridOrderIds(ids);
		},
		onFiltered(ids) {
			this.setFilteredGridOrderIds(ids);
		},
		showWaybill() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getWaybill(ids);
		},
		showSingleWaybill(id) {
			this.getWaybill([id]);
		},
		showPackageLabel() {
			let ids = this.selectedOrders.map(x => x.orderId);
			this.getPackageLabel(ids);
		},
		showSinglePackageLabel(id) {
			this.getPackageLabel([id]);
		},
		getEdiIcon(item) {
			let transmission = Boolean(item.edi.out_edipartId);
			if (transmission && item.edi.outboundSchedule.isSent)
				return { icon: 'tick', color: 'green' };
			else
				return { icon: 'time', color: 'orange' };
		},
		getProperty(object, propKey) {
			if (!object) return undefined;
			return object[propKey];
		},
		getStatusIcon(item) {
			return goodsTypesIcons(item.goodsType);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		addressGetter(party) {
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		orderInstructionSent(item) {
			if (!item.edi)
				return false;
			return this.hasInstruction(item);
		},
		orderInstructionScheduled(item) {
			if (!item.edi)
				return false;
			return this.isScheduled(item);
		},
		isSent(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } = item.edi.outboundSchedule;
			return !isScheduled && isSent;
		},
		isUnscheduled(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } = item.edi.outboundSchedule;
			return !isScheduled && !isSent;
		},
		isScheduled(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } =item.edi.outboundSchedule;
			return isScheduled && !isSent;
		},
		hasInstruction(item) {
			return item.edi.transmissions.some(x => x.messageTypeNo === edi.outboundEdiTypes.instruction);
		},
		transporterHasEdipart() {
			return Boolean(this.tripDetails.vendor.edipartId);
		},
		dateGetter(actual, estimated) {
			if (dateExists(actual)) return actual.date;
			if (dateExists(estimated)) return estimated.date;
			return undefined;
		},
		onGridDelete() {
			this.onRemoveOrders(this.$refs.grid.getSelectedItems());
		},
		setEditOrder(id) {
			let order = this.tripDetails.orders.find(x => x.id === id);
			if (!order) return;
			eventBus.$emit('setEditOrder', order.orderId);
		},

		toggleRemoveTripOrder(value) {
			this.confirmRemoveTripOrderVisible = value;
		},
		onScheduleOrder(obj) {
			this.scheduleSubOrderForEdi(obj);
		},
		onUnscheduleOrder(obj) {
			this.unscheduleSubOrderForEdi(obj);
		},
		onSendTripOrdersOnEdi() {
			this.sendTripOnEdi();
			this.$notify({
				type: 'success',
				title: this.$tc('tripHasBeenScheduledForEdi', this.tripDetails.tripNumber)
			});
		},
		onRemoveOrders(orders, merge = false) {
			if (!orders) return;
			if (Array.isArray(orders))
				this.ordersToRemove = orders;
			else
				this.ordersToRemove = [orders];
			this.mergeOrder = merge;
			if (this.tripDetails.type.internalCode === 'external') {
				let timeCheck = moment.utc().subtract(5,'minutes')
					.valueOf();
				let hasSentOrders = this.ordersToRemove.some(x => x.createdAt < timeCheck || x.sentStatus);
				if (hasSentOrders)
					this.toggleRemoveTransporterOrder(true);
				else
					this.confirmRemoveOrdersFromTransporter();
			} else {
				this.toggleRemoveTripOrder(true);
			}
		},
		confirmRemoveOrdersFromTrip() {
			try {
				this.removeOrdersFromTrip({ orders: this.ordersToRemove, merge: this.mergeOrder });
				this.$notify({
					type: 'success',
					title: `${this.$t('orderRemovedFromTrip')}`
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToRemoveOrderTrip')
				});
			}
		},
		showMoveLoadedOrder(order) {
			this.selectedOrder = order;
			this.moveLoadedOrderVisible = true;
		},
		async onMoveOrderToDifferentTrip(data) {
			this.moveLoadedOrderProcessing = true;
			try {
				await this.moveOrderToDifferentTrip(data);
				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: data.newTrip.tripNumber })}`
				});
				this.moveLoadedOrderVisible = false;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip', { number: data.newTrip.tripNumber }),
					duration: -1
				});
			}
			this.moveLoadedOrderProcessing = false;
		}
	}
};
</script>


<style lang="scss" scoped>
.order-list {
	.order-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.order-list-panel-header {
		width: 145px;
	}
	.order-list-summary {
		display:grid;
		padding: 15px;
		grid-template-columns: repeat(4, max-content);
		grid-column-gap: 10px;
		margin-right: auto;
		width: 225px;
	}
	.order-list-sub-header {
		height: 100%;
		.buttons-with-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(3, minmax(100px, max-content)) auto repeat(2, minmax(100px, max-content));
			grid-column-gap: 10px;
		}
		.buttons-no-text {
			display:grid;
			padding: 0px 10px;
			height: 100%;
			align-items: center;
			grid-template-columns: repeat(3, minmax(min-content, max-content)) auto repeat(2, minmax(min-content, max-content));
			grid-column-gap: 10px;
		}
	}
	.order-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
